import { t } from '@bia-auction/shared-i18n';
import type { SaleLot } from '@bia-auction/shared-schema';
import {
  AmountDialog,
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from '@bia-auction/ui';
import { Link } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { currencySymbol, formatKurus } from '../lib/format';
import { PaymentDialog } from './PaymentDialog';

/**
 * The bid controls for one lot — bid, jump, early bid, deposit — as a fragment the caller
 * lays out.
 *
 * Shared by the auction room's lot card and the vehicle detail page (redesign-vehicle.md §7,
 * whose approved mock puts `[ Teklif ver ]` in the detail page's price panel). One copy, so
 * the two surfaces cannot drift on what a bidder is allowed to press — in particular the
 * anonymous case, where every action needs a session and a disabled button would just be a
 * puzzle.
 *
 * The caller owns the container: `stacked` sizes the buttons for a narrow sidebar, the
 * default for a wrapping row.
 */
export function LotActions({
  saleId,
  lot,
  isOpen,
  saleActive,
  canDeposit,
  authenticated,
  stacked = false,
}: {
  saleId: string;
  lot: SaleLot;
  /** Live-bid window: the sale is running, the lot is OPEN, and its deadline hasn't passed. */
  isOpen: boolean;
  saleActive: boolean;
  canDeposit: boolean;
  authenticated: boolean;
  stacked?: boolean;
}) {
  const [depositOpen, setDepositOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Money/count entry through validated dialogs (redesign-bidder.md §0 D2) — the app's
  // last two native prompt()s died here.
  const [dialog, setDialog] = useState<null | 'jump' | 'early' | 'confirm'>(null);
  /**
   * The figure quoted when the confirmation opened (M19).
   *
   * Held separately from `lot`, which keeps re-rendering: a `bid.placed` frame arriving while
   * the dialog is open would otherwise change the number under the reader's eyes and commit
   * whatever it had become. A bid is binding, so the amount that binds has to be the one that
   * was actually read — and if the price has moved, the server's `StalePrice` refusal is the
   * correct outcome rather than a silent upgrade.
   */
  const [quoted, setQuoted] = useState<number | null>(null);

  // No control applies once the lot leaves the auction — including **REMOVED**, which the room
  // never had to think about (it filters removed lots out of the list) but the detail page
  // does: a bidder can be sitting on the page when the admin pulls the lot, and the deposit
  // CTA has no `isOpen` gate to fall back on. The caller renders the state badge.
  const finished = lot.state === 'SOLD' || lot.state === 'UNSOLD' || lot.state === 'REMOVED';
  // ≥44px touch targets on phones (redesign-bidder.md §0 D1).
  const btn = stacked ? 'h-11 w-full' : 'h-11 sm:h-9';

  // What a `+1` press will commit. `lot.increment` is the **effective** next step, already
  // resolved server-side against the bands and any per-lot override (`lib/sale-mapper.ts`),
  // so this is the engine's own figure rather than a second implementation of the rules.
  // Every money field is null on the masked projection, which never renders a bid control.
  const nextAmount =
    lot.increment == null ? null : (lot.currentTopBid ?? lot.startingPrice ?? 0) + lot.increment;

  /**
   * The idempotency key for the attempt in progress, with the request it belongs to (M19).
   *
   * A bid the server committed but whose response never arrived leaves the bidder looking at
   * an error and pressing again — which is the duplicate the key exists to prevent. So the key
   * is **kept after a failure** and reused by the next press of the same request, and
   * **discarded after a success**, where the next press is a genuine second bid.
   *
   * Scoped by the request itself, not just the path: retrying a jump with a different target
   * is a new intent, and reusing the key there would replay the first jump's amount.
   */
  const attempt = useRef<{ signature: string; key: string; quote: number | null } | null>(null);

  const act = async (path: string, body?: unknown) => {
    const signature = `${path}:${JSON.stringify(body ?? {})}`;
    if (attempt.current?.signature !== signature) {
      attempt.current = { signature, key: crypto.randomUUID(), quote: quoted };
    }
    const idempotencyKey = attempt.current.key;

    setBusy(true);
    try {
      await api(`/sales/${saleId}/lots/${lot.id}${path}`, {
        method: 'POST',
        body: body ?? {},
        idempotencyKey,
      });
      attempt.current = null; // committed — the next press is a new bid, not a retry
    } catch (err) {
      // A `StalePrice` refusal is a **definitive non-commit**: the server compared the quote
      // and declined before touching anything. Keeping the attempt would make the next press
      // a "retry" that reopens the same stale figure and is refused again, forever — while the
      // toast asks the bidder to check the new amount. So the gesture is discarded here and
      // the next press quotes afresh. Only *ambiguous* failures (a transport error, a timeout)
      // keep their key, because only those might have committed.
      if (err instanceof ApiError && err.code === 'StalePrice') attempt.current = null;
      toast(err instanceof Error ? err.message : t('bidder.detail.actionFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  // The anonymous branch stays ahead of the finished check: signing in is what reveals the
  // masked price, which is worth offering on a decided lot too, and a `/login` link cannot
  // act on a lot that is gone.
  if (!authenticated) {
    return (
      // `col-span-2` so the (long) Turkish CTA gets the whole row when the caller lays the
      // controls out two-up; inert in a flex container.
      <Button
        asChild
        variant="brand"
        className={stacked ? 'col-span-2 h-11 w-full' : 'h-11 flex-1 sm:h-9'}
      >
        <Link to="/login">{t('bidder.masked.loginToBid')}</Link>
      </Button>
    );
  }
  if (finished) return null;

  return (
    <>
      <Button
        className={btn}
        disabled={busy || !isOpen}
        onClick={() => {
          // An unresolved `/bids` attempt means the last press may have committed with its
          // response lost — so this press is that gesture's retry, and it has to carry the
          // *same* figure. Re-quoting from the lot would change the body, change the request
          // signature, mint a fresh key, and commit a second binding bid: the duplicate the
          // key exists to prevent, arriving through the confirmation.
          const retry = attempt.current?.signature.startsWith('/bids:') ? attempt.current : null;
          setQuoted(retry ? retry.quote : nextAmount);
          setDialog('confirm');
        }}
      >
        {t('bidder.detail.bid')}
      </Button>
      <Button
        className={btn}
        variant="outline"
        disabled={busy || !isOpen}
        onClick={() => setDialog('jump')}
      >
        {t('bidder.detail.jump')}
      </Button>
      {saleActive && !isOpen && lot.state === 'SCHEDULED' && (
        <Button className={btn} variant="outline" disabled={busy} onClick={() => setDialog('early')}>
          {t('bidder.detail.early')}
        </Button>
      )}
      {canDeposit && (
        <Button
          className={btn}
          variant="secondary"
          disabled={busy}
          onClick={() => setDepositOpen(true)}
        >
          {t('bidder.detail.deposit')}
        </Button>
      )}

      {/* The +1 bid is the one control that committed money on a single press, with nothing
          said about what it commits to (audit Y-08). The step shown is the server-resolved
          effective increment carried on the lot — bands and per-lot overrides included — so
          the figure here is the one the engine will apply, not a client re-derivation. */}
      <ConfirmDialog
        open={dialog === 'confirm'}
        onOpenChange={(o) => !o && setDialog(null)}
        title={t('bidder.detail.confirmTitle')}
        description={
          <>
            {/* docs/15 §1: tabular-nums on any figure, so the digits do not shift width. */}
            <p className="font-medium tabular-nums text-foreground">
              {t('bidder.detail.confirmAmount', {
                amount: formatKurus(quoted, lot.currency),
              })}
            </p>
            <p className="mt-1">{t('bidder.detail.binding')}</p>
          </>
        }
        confirmLabel={t('bidder.detail.confirmBid')}
        cancelLabel={t('common.cancel')}
        busy={busy}
        onConfirm={() => {
          setDialog(null);
          // The frozen quote travels with the request — the same number that was displayed,
          // not whatever the lot has become since. If the price moved, the server refuses.
          void act('/bids', quoted === null ? {} : { expectedAmount: quoted });
        }}
      />

      <AmountDialog
        open={dialog === 'jump'}
        onOpenChange={(o) => !o && setDialog(null)}
        title={t('bidder.detail.jump')}
        description={`${t('bidder.detail.jumpRounding')} ${t('bidder.detail.binding')}`}
        label={t('bidder.detail.jumpPrompt', { currency: currencySymbol(lot.currency) })}
        // `startingPrice` is null only on the anonymous masked projection (M11), which never
        // renders a bid control — the `?? 0` is a type guard, not a real floor.
        min={(lot.currentTopBid ?? lot.startingPrice ?? 0) + 1}
        minLabel={t('bidder.detail.mustExceedTop')}
        confirmLabel={t('bidder.detail.jump')}
        cancelLabel={t('common.cancel')}
        onSubmit={(target) => {
          void act('/bids/jump', { target });
          setDialog(null);
        }}
      />
      {dialog === 'early' && (
        <EarlyBidDialog
          onOpenChange={(o) => !o && setDialog(null)}
          onSubmit={(times) => {
            void act('/bids/early', { times });
            setDialog(null);
          }}
        />
      )}

      <PaymentDialog
        open={depositOpen}
        onOpenChange={setDepositOpen}
        title={t('bidder.deposit.title')}
        path={`/sales/${saleId}/lots/${lot.id}/deposits`}
      />
    </>
  );
}

/** Early-bid step count entry (replaces the last native prompt()): integer ≥ 1. */
function EarlyBidDialog({
  onOpenChange,
  onSubmit,
}: {
  onOpenChange: (open: boolean) => void;
  onSubmit: (times: number) => void;
}) {
  const [raw, setRaw] = useState('1');
  const [touched, setTouched] = useState(false);
  const times = Number(raw);
  const invalid = !Number.isInteger(times) || times <= 0;

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            setTouched(true);
            if (!invalid) onSubmit(times);
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('bidder.detail.early')}</DialogTitle>
          </DialogHeader>
          {/* Every path that commits a bid says so — an early bid places N of them at once,
              so it is the least obvious of the three. */}
          <p className="text-sm text-muted-foreground">{t('bidder.detail.binding')}</p>
          <div className="space-y-1.5">
            <Label htmlFor="early-times">{t('bidder.detail.earlyPrompt')}</Label>
            <Input
              id="early-times"
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              autoFocus
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              aria-invalid={touched && invalid}
              aria-describedby={touched && invalid ? 'early-times-error' : undefined}
            />
            {touched && invalid && (
              <div id="early-times-error" role="alert" className="text-xs text-destructive">
                {t('bidder.detail.earlyInvalid')}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit">{t('bidder.detail.early')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
